import React, { useState, useEffect, useCallback } from 'react';

import { Alert } from '@shared/components/bootstrap';
import { UserRole } from '@shared/types/user_role';

import { Roles } from '@admin/components/helpers/roles';
import { client } from '@admin/libraries/apollo';
import { Status, useAccountCustomersUpdateMutation } from '@admin/schema';

import { ConsentInfoModal } from './edit_form/consent_info_modal';
import { CustomerRow } from './edit_form/customer_row';
import { ConsentField, Customer, EditCustomersFormProps, Phone, PhoneType } from './edit_form/types';
import {
  buildCustomerInput,
  getCustomersToEdit,
  getCustomersToSave,
  getEmptyCustomer,
  getEmptyPhone,
  getNonDeletedPhones,
  validateCustomers,
} from './edit_form/utils';

/**
 * Form component for editing customers on an account.
 *
 * @param props - The component props.
 * @returns The EditCustomersForm component.
 */
export const EditCustomersForm: React.FC<EditCustomersFormProps> = ({ account, onSuccess, onError }) => {
  const [customers, setCustomers] = useState<Customer[]>(() =>
    account?.customers ? getCustomersToEdit(account.customers) : [],
  );
  const [consentInfoModal, setConsentInfoModal] = useState<ConsentField | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  // Pundit guard: AccountPolicy#customers_update? (accountCustomersUpdate, ENG-740)
  const [updateCustomers, { loading: saving }] = useAccountCustomersUpdateMutation({ client });

  useEffect(() => {
    if (account?.customers) {
      setCustomers(getCustomersToEdit(account.customers));
    }
  }, [account]);

  const canSave = useCallback((): boolean => {
    if (!customers.length) return false;
    return customers.some((customer) => getNonDeletedPhones(customer.phones).some((phone) => phone.primary));
  }, [customers]);

  const addCustomer = () => {
    setCustomers((prev) => [...prev, getEmptyCustomer(account.id)]);
  };

  const addPhone = (customerTempId: string) => {
    setCustomers((prev) =>
      prev.map((customer) =>
        customer.tempId === customerTempId ? { ...customer, phones: [...customer.phones, getEmptyPhone()] } : customer,
      ),
    );
  };

  const deletePhone = (customerTempId: string, phone: Phone) => {
    setCustomers((prev) =>
      prev.map((customer) => {
        if (customer.tempId !== customerTempId) return customer;

        let updatedPhones: Phone[];
        if (phone.id) {
          updatedPhones = customer.phones.map((p) => (p.tempId === phone.tempId ? { ...p, deleted: true } : p));
        } else {
          updatedPhones = customer.phones.filter((p) => p.tempId !== phone.tempId);
        }

        // Ensure at least one phone exists
        if (getNonDeletedPhones(updatedPhones).length === 0) {
          updatedPhones.push(getEmptyPhone());
        }

        return { ...customer, phones: updatedPhones };
      }),
    );
  };

  const togglePrimaryPhone = (phoneTempId: string) => {
    setCustomers((prev) =>
      prev.map((customer) => ({
        ...customer,
        phones: customer.phones.map((phone) => ({
          ...phone,
          primary: phone.tempId === phoneTempId ? !phone.primary : false,
        })),
      })),
    );
  };

  const togglePrimaryEmail = (customerTempId: string) => {
    setCustomers((prev) =>
      prev.map((customer) => ({
        ...customer,
        primary_email: customer.tempId === customerTempId ? !customer.primary_email : false,
      })),
    );
  };

  const updateCustomerField = (customerTempId: string, field: keyof Customer, value: string) => {
    setCustomers((prev) =>
      prev.map((customer) => (customer.tempId === customerTempId ? { ...customer, [field]: value } : customer)),
    );
  };

  const updatePhoneField = (
    customerTempId: string,
    phoneTempId: string,
    field: keyof Phone,
    value: string | PhoneType,
  ) => {
    setCustomers((prev) =>
      prev.map((customer) =>
        customer.tempId === customerTempId
          ? {
              ...customer,
              phones: customer.phones.map((phone) =>
                phone.tempId === phoneTempId ? { ...phone, [field]: value } : phone,
              ),
            }
          : customer,
      ),
    );
  };

  const handleConsentFieldChange = (
    customerTempId: string,
    phoneTempId: string,
    consentField: ConsentField,
    checked: boolean,
  ) => {
    setCustomers((prev) =>
      prev.map((customer) => {
        if (customer.tempId !== customerTempId) return customer;

        return {
          ...customer,
          phones: customer.phones.map((phone) => {
            if (phone.tempId !== phoneTempId) return phone;

            const updates: Partial<Phone> = { [consentField]: checked };

            // Handle consent field dependencies
            if (consentField === 'transactional_sms' && !checked) {
              updates.commercial_sms = false;
            } else if (consentField === 'transactional_call' && !checked) {
              updates.commercial_call = false;
            } else if (consentField === 'commercial_sms' && checked) {
              updates.transactional_sms = true;
            } else if (consentField === 'commercial_call' && checked) {
              updates.transactional_call = true;
            }

            return { ...phone, ...updates };
          }),
        };
      }),
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationErrors([]);

    const data = getCustomersToSave(customers);
    const report = validateCustomers(data);

    if (!report.isValid) {
      setValidationErrors(report.messages);
      return;
    }

    try {
      const result = await updateCustomers({
        variables: {
          accountID: String(account.id),
          customers: data.map(buildCustomerInput),
        },
      });

      const { status, error } = result.data?.accountCustomersUpdate ?? {};

      if (status === Status.Ok) {
        onSuccess();
      } else {
        onError({ error: { data: { message: error || 'An error occurred while saving customers.' } } });
      }
    } catch {
      onError({ error: { data: { message: 'An error occurred while saving customers.' } } });
    }
  };

  const handleCancel = (e: React.MouseEvent) => {
    e.preventDefault();
    window.location.hash = `/accounts/${account.id}`;
  };

  return (
    <>
      {consentInfoModal && <ConsentInfoModal field={consentInfoModal} onClose={() => setConsentInfoModal(null)} />}

      <form className="form-horizontal" role="form" onSubmit={handleSubmit}>
        <div className="row">
          <div className="col-sm-12">
            <div className="panel">
              <div className="panel-heading">
                <h3 className="panel-title">Details</h3>
              </div>

              <div className="panel-body">
                {validationErrors.length > 0 && (
                  <Alert style="danger">
                    {validationErrors.map((error, index) => (
                      <div key={index}>{error}</div>
                    ))}
                  </Alert>
                )}

                <table className="table" id="edit-customers-table">
                  <tbody>
                    {customers.map((customer) => (
                      <CustomerRow
                        key={customer.tempId}
                        customer={customer}
                        onUpdateCustomerField={updateCustomerField}
                        onUpdatePhoneField={updatePhoneField}
                        onTogglePrimaryPhone={togglePrimaryPhone}
                        onTogglePrimaryEmail={togglePrimaryEmail}
                        onDeletePhone={deletePhone}
                        onAddPhone={addPhone}
                        onConsentFieldChange={handleConsentFieldChange}
                        onConsentInfoClick={setConsentInfoModal}
                      />
                    ))}
                  </tbody>
                </table>

                <Roles hide={[UserRole.L1Agent]}>
                  {/* Pundit guard: AccountPolicy#customers_update? (accountCustomersUpdate, ENG-740) */}
                  <button className="btn btn-primary" onClick={addCustomer} type="button">
                    Add Customer
                  </button>
                </Roles>
              </div>

              <div className="panel-footer text-right">
                <button type="button" className="btn btn-default" onClick={handleCancel}>
                  Cancel
                </button>{' '}
                {/* Pundit guard: AccountPolicy#customers_update? (accountCustomersUpdate, ENG-740) */}
                <input
                  className="btn btn-primary"
                  disabled={!canSave() || saving}
                  type="submit"
                  value={saving ? 'Saving...' : 'Save'}
                />
              </div>
            </div>
          </div>
        </div>
      </form>
    </>
  );
};
